/**
 * React hook for multiplayer cursor presence via WebSocket.
 *
 * Connects to /ws/presence, authenticates, joins a room,
 * and broadcasts local cursor positions while tracking peers.
 */

import { useAuthRuntime } from "@frontend/components/auth/auth-runtime"
import type { ClientMessage, PeerInfo, ServerMessage } from "@shared/ws/types.ts"
import type { TileViewer } from "@virdx/wsi-viewer"
import { useCallback, useEffect, useRef, useState } from "react"

export interface UsePresenceOptions {
  projectId: string | undefined
  caseId: string | undefined
  viewerRef: React.RefObject<TileViewer | null>
  enabled: boolean
  /** Reactive signal that flips to true once viewerRef.current is populated. */
  isViewerReady: boolean
}

export interface UsePresenceResult {
  peers: PeerInfo[]
  isConnected: boolean
  peersRef: React.RefObject<Map<string, PeerInfo>>
}

const THROTTLE_MS = 33 // ~30fps
const PING_INTERVAL_MS = 25_000
const RECONNECT_BASE_MS = 1_000
const RECONNECT_MAX_MS = 10_000

export function usePresence({
  projectId,
  caseId,
  viewerRef,
  enabled,
  isViewerReady
}: UsePresenceOptions): UsePresenceResult {
  const { getAccessToken } = useAuthRuntime()
  const getAccessTokenRef = useRef(getAccessToken)
  useEffect(() => {
    getAccessTokenRef.current = getAccessToken
  }, [getAccessToken])

  const [peers, setPeers] = useState<PeerInfo[]>([])
  const [isConnected, setIsConnected] = useState(false)

  const wsRef = useRef<WebSocket | null>(null)
  const peersRef = useRef<Map<string, PeerInfo>>(new Map())
  const lastCursorSendRef = useRef(0)
  const reconnectDelayRef = useRef(RECONNECT_BASE_MS)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mountedRef = useRef(true)

  const syncPeersToState = useCallback(() => {
    setPeers(Array.from(peersRef.current.values()))
  }, [])

  const connect = useCallback(async () => {
    if (!(enabled && projectId && caseId && mountedRef.current)) return

    // Clean up any existing connection
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }

    let token: string
    try {
      token = (await getAccessTokenRef.current()) ?? ""
    } catch {
      token = ""
    }

    const protocol = location.protocol === "https:" ? "wss:" : "ws:"
    // The socket handle is stored in `wsRef` and closed by the connect/disconnect
    // effect cleanup below; react-doctor cannot see across the useCallback boundary.
    // react-doctor-disable-next-line react-doctor/effect-needs-cleanup
    const ws = new WebSocket(`${protocol}//${location.host}/ws/presence`)
    wsRef.current = ws

    ws.onopen = () => {
      const authMsg: ClientMessage = { type: "auth", token }
      ws.send(JSON.stringify(authMsg))
    }

    ws.onmessage = (event) => {
      let msg: ServerMessage
      try {
        msg = JSON.parse(event.data)
      } catch {
        return
      }

      switch (msg.type) {
        case "auth_ok": {
          // Join the room
          const joinMsg: ClientMessage = {
            type: "join",
            projectId: projectId!,
            caseId: caseId!
          }
          ws.send(JSON.stringify(joinMsg))
          break
        }

        case "auth_fail": {
          ws.close()
          break
        }

        case "room_joined": {
          peersRef.current.clear()
          for (const peer of msg.peers) {
            peersRef.current.set(peer.userId, peer)
          }
          syncPeersToState()
          setIsConnected(true)
          reconnectDelayRef.current = RECONNECT_BASE_MS

          // Start ping interval
          if (pingTimerRef.current) clearInterval(pingTimerRef.current)
          pingTimerRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: "ping" } as ClientMessage))
            }
          }, PING_INTERVAL_MS)
          break
        }

        case "peer_joined": {
          peersRef.current.set(msg.userId, {
            userId: msg.userId,
            name: msg.name,
            color: msg.color,
            x: null,
            y: null
          })
          syncPeersToState()
          break
        }

        case "peer_cursor": {
          const existing = peersRef.current.get(msg.userId)
          if (existing) {
            existing.x = msg.x
            existing.y = msg.y
          }
          // No React state update — cursor overlay reads from ref via rAF
          break
        }

        case "peer_cursor_leave": {
          const existing = peersRef.current.get(msg.userId)
          if (existing) {
            existing.x = null
            existing.y = null
          }
          break
        }

        case "peer_left": {
          peersRef.current.delete(msg.userId)
          syncPeersToState()
          break
        }

        case "pong":
          break
      }
    }

    ws.onclose = () => {
      setIsConnected(false)
      peersRef.current.clear()
      syncPeersToState()

      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current)
        pingTimerRef.current = null
      }

      // Reconnect with exponential backoff
      if (mountedRef.current && enabled) {
        reconnectTimerRef.current = setTimeout(() => {
          reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, RECONNECT_MAX_MS)
          void connect()
        }, reconnectDelayRef.current)
      }
    }

    ws.onerror = () => {
      // onclose will fire after onerror
    }
  }, [enabled, projectId, caseId, syncPeersToState])

  // Connect/disconnect on dependency changes
  useEffect(() => {
    mountedRef.current = true

    if (enabled && projectId && caseId) {
      void connect()
    }

    return () => {
      mountedRef.current = false
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current)
        pingTimerRef.current = null
      }
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
      peersRef.current.clear()
      setPeers([])
      setIsConnected(false)
    }
  }, [enabled, projectId, caseId, connect])

  // Subscribe to viewer pointer events and broadcast cursor
  useEffect(() => {
    if (!enabled) return
    const viewer = viewerRef.current
    if (!viewer) return

    const handlePointerMove = (e: { imageX: number; imageY: number }) => {
      const now = Date.now()
      if (now - lastCursorSendRef.current < THROTTLE_MS) return
      lastCursorSendRef.current = now

      const ws = wsRef.current
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "cursor", x: e.imageX, y: e.imageY } as ClientMessage))
      }
    }

    const handlePointerLeave = () => {
      const ws = wsRef.current
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "cursor_leave" } as ClientMessage))
      }
    }

    viewer.on("pointer-move", handlePointerMove)
    viewer.on("pointer-leave", handlePointerLeave)

    return () => {
      viewer.off("pointer-move", handlePointerMove)
      viewer.off("pointer-leave", handlePointerLeave)
    }
  }, [enabled, viewerRef])

  return { peers, isConnected, peersRef }
}
